import { concat, fn } from '@ember/helper';
import sort from 'bcl/helpers/sort';
import { on } from '@ember/modifier';
import { Input } from '@ember/component';
import lte from 'ember-truth-helpers/helpers/lte';
import amountName from 'bcl/helpers/amount-name';
import SelectProductgroup from 'bcl/components/input/select-productgroup';
import StocktakingMobile from 'bcl/components/stocktaking-mobile';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type ProductGroupModel from 'bcl/models/product-group';
import type StocktakingModel from 'bcl/models/stocktaking';
import type LocationModel from 'bcl/models/location';
import type StocktakingEntryModel from 'bcl/models/stocktaking-entry';
import type StocktakingService from 'bcl/services/stocktaking';
import ContinuousSavingStocktaking from 'bcl/modifiers/continuous-saving-stocktaking';

interface StocktakingNameSignature {
  // The arguments accepted by the component
  Args: {
    model: { stocktaking: StocktakingModel; location: LocationModel };
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class StocktakingNameComponent extends Component<StocktakingNameSignature> {
  @service declare store: Store;
  @service declare stocktaking: StocktakingService;

  @tracked showSingleSize = false;

  @action toggleSingleSize() {
    this.showSingleSize = !this.showSingleSize;
  }
  @action markAllAsProposed() {
    const entries = this.entries ?? [];
    for (let i = 0; i < entries.length; i++) {
      const ste = entries[i]!;
      const expectedAmount = ste?.location_item.proposal_amount ?? 0;
      if (ste?.amount !== expectedAmount || !ste.hasSetAmount) {
        ste.amount = expectedAmount;
        ste.hasSetAmount = true;
        ste.changed_at = new Date();
      }
    }
  }
  @tracked fastEntry = false;
  @action toggleFastEntry() {
    this.fastEntry = !this.fastEntry;
  }
  get location() {
    return this.args.model.location;
  }
  get stocktakingModel() {
    return this.args.model.stocktaking;
  }
  get entries() {
    const loc = this.location;
    return this.stocktakingModel.stocktakingEntries.filter((ste) => {
      return ste.location_item.location == loc;
    });
  }

  @tracked declare productGroup: ProductGroupModel | null;
  @action changeProductGroup(group: ProductGroupModel | null) {
    this.productGroup = group;
  }
  get filteredEntries() {
    const entries = this.entries ?? [];
    if (!this.productGroup) {
      return entries;
    }
    return entries.filter((ste) => {
      return ste.location_item.item?.product_group.id === this.productGroup?.id;
    });
  }

  @action add(ste: StocktakingEntryModel, amount: number) {
    const num = +(ste.virtual_amount ?? 0);
    ste.virtual_amount = num + amount;
    ste.changed_at = new Date();
  }
  @action add_single(ste: StocktakingEntryModel, amount: number) {
    const num = +(ste.virtual_amount_single ?? 0);
    ste.virtual_amount_single = num + amount;
    ste.changed_at = new Date();
  }

  <template>
    <div
      class="px-4 lg:px-8"
      {{ContinuousSavingStocktaking this.stocktakingModel}}
    >
      {{#if this.fastEntry}}
        <StocktakingMobile @controller={{this}} />
      {{else}}
        <button
          {{on "click" this.markAllAsProposed}}
          type="button"
          class="inline-block rounded-md bg-green-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >Standard eintragen</button>
        <button
          {{on "click" this.toggleFastEntry}}
          type="button"
          class="inline-block rounded-md bg-green-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >Schnelles eintragen</button>
        <SelectProductgroup
          @label=""
          @name="productGroup"
          @onChange={{this.changeProductGroup}}
          @hasAllOption={{true}}
          @value={{this.productGroup}}
        />

        <div class="flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >Name</th>

                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >Gebinde
                      <button
                        {{on "click" this.toggleSingleSize}}
                        type="button"
                        class="ml-2 rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >Einzel eingeben</button></th>
                    {{#if this.showSingleSize}}
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >Einzel</th>
                    {{/if}}
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >Standard</th>

                  </tr>
                </thead>
                <tbody class="bg-white">
                  {{#each
                    (sort this.filteredEntries "location_item.item.name")
                    as |entry|
                  }}
                    <tr class="even:bg-gray-50">
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                      >{{entry.location_item.item.name}}
                        ({{entry.location_item.item.gebinde}})
                      </td>

                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        <label
                          for="{{concat 'count-' entry.id}}"
                          class="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:text-right sr-only"
                        >gezählte Menge für
                          {{entry.location_item.item.name}}</label>

                        <span
                          class="isolate inline-flex rounded-md shadow-sm mr-3"
                        >
                          <button
                            {{on "click" (fn this.add entry -5)}}
                            type="button"
                            class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                          >-5</button>
                          <button
                            {{on "click" (fn this.add entry -1)}}
                            type="button"
                            class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                          >-1</button>
                        </span>
                        <Input
                          id="{{concat 'count-' entry.id}}"
                          @type="text"
                          @value={{entry.virtual_amount}}
                          class="inline w-16 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        />
                        <span
                          class="isolate inline-flex rounded-md shadow-sm ml-3"
                        >
                          <button
                            {{on "click" (fn this.add entry 1)}}
                            type="button"
                            class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                          >+1</button>
                          <button
                            {{on "click" (fn this.add entry 5)}}
                            type="button"
                            class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                          >+5</button>
                        </span>
                      </td>
                      {{#if this.showSingleSize}}
                        <td
                          class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                        >
                          <label
                            for="{{concat 'count-' entry.id}}single"
                            class="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:text-right sr-only"
                          >gezählte Einzel-Menge für
                            {{entry.location_item.item.name}}</label>

                          <span
                            class="isolate inline-flex rounded-md shadow-sm mr-2"
                          >
                            <button
                              {{on "click" (fn this.add_single entry -5)}}
                              type="button"
                              class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                            >-5</button>
                            <button
                              {{on "click" (fn this.add_single entry -1)}}
                              type="button"
                              class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                            >-1</button>
                          </span>
                          <Input
                            id="{{concat 'count-' entry.id}}single"
                            @type="text"
                            @value={{entry.virtual_amount_single}}
                            class="inline w-16 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                          />
                          <span
                            class="isolate inline-flex rounded-md shadow-sm ml-2"
                          >
                            <button
                              {{on "click" (fn this.add_single entry 1)}}
                              type="button"
                              class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                            >+1</button>
                            <button
                              {{on "click" (fn this.add_single entry 5)}}
                              type="button"
                              class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                            >+5</button>
                          </span>
                        </td>
                      {{/if}}
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {{entry.location_item.virtual_proposal_amount}}
                        <span class="font-bold">
                          {{amountName
                            entry.location_item.item
                            entry.location_item.virtual_proposal_amount
                          }}
                        </span>
                      </td>
                    </tr>
                  {{/each}}

                </tbody>
              </table>
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
